import { useEffect, useRef, useState } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { REGEXP_ONLY_DIGITS } from "input-otp"
import {
  ArrowLeft,
  Building2,
  Check,
  Loader2,
  ShieldCheck,
  UserCheck,
} from "lucide-react"
import { useForm, UseFormReturn } from "react-hook-form"
import { z } from "zod"

import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@/components/ui"
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp"
import { useTranslation } from "@/i18n"
import { cn } from "@/lib/utils/utils"

// Type definitions for form data
type LoginFormData = {
  email_address: string
  first_name?: string
  last_name?: string
  user_type?: "employer" | "agent"
}

type OtpFormData = {
  otp: string
}

interface UserEmailAuthFormProps {
  handleSubmit: (data: LoginFormData) => void
  handleVerify: (data: { otp: string }) => void
  handleResendOtp?: () => void
  onBack?: () => void
  isLoading?: boolean
  isOtpSent: boolean
  sentEmailAddress?: string
  mode?: "sign_in" | "sign_up"
}

export const UserEmailAuthForm = ({
  handleSubmit,
  handleVerify,
  handleResendOtp,
  onBack,
  isLoading = false,
  isOtpSent,
  sentEmailAddress = "",
  mode = "sign_in",
}: UserEmailAuthFormProps) => {
  const { t } = useTranslation()
  const isSignUp = mode === "sign_up"

  const loginSchema = z.object({
    email_address: z.string().email(t("auth.emailAuth.validationEmail")),
    first_name: isSignUp
      ? z.string().min(1, t("onboarding.profileDetail.validationFirstName"))
      : z.string().optional(),
    last_name: isSignUp
      ? z.string().min(1, t("onboarding.profileDetail.validationLastName"))
      : z.string().optional(),
    user_type: isSignUp
      ? z.enum(["employer", "agent"], {
          errorMap: () => ({
            message: t("onboarding.profileDetail.validationSelectUserType"),
          }),
        })
      : z.enum(["employer", "agent"]).optional(),
  })

  const otpSchema = z.object({
    otp: z.string().length(6, t("auth.emailAuth.validationOtp")),
  })

  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email_address: "",
      first_name: "",
      last_name: "",
      user_type: "employer",
    },
  })

  const otpForm = useForm<OtpFormData>({
    resolver: zodResolver(otpSchema),
    defaultValues: { otp: "" },
  })

  const onOtpSubmit = (data: OtpFormData) => {
    handleVerify(data)
  }

  const onLogin = (data: LoginFormData) => {
    handleSubmit(data)
  }

  const showOtpScreen = isOtpSent

  // OTP Verification Screen
  if (showOtpScreen) {
    return (
      <OtpVerificationScreen
        isLoading={isLoading}
        onBack={onBack}
        sentEmailAddress={sentEmailAddress}
        otpForm={otpForm}
        onOtpSubmit={onOtpSubmit}
        onResendOtp={handleResendOtp}
      />
    )
  }

  // Login Screen
  return (
    <LoginScreen
      isLoading={isLoading}
      loginForm={loginForm}
      onLogin={onLogin}
      mode={mode}
    />
  )
}

interface LoginScreenProps {
  isLoading: boolean
  loginForm: UseFormReturn<LoginFormData>
  onLogin: (data: LoginFormData) => void
  mode: "sign_in" | "sign_up"
}

const LoginScreen = ({
  isLoading,
  loginForm,
  onLogin,
  mode,
}: LoginScreenProps) => {
  const { t } = useTranslation()
  const isSignUp = mode === "sign_up"
  const captchaRef = useRef<HTMLDivElement | null>(null)
  const [hasCaptchaChallenge, setHasCaptchaChallenge] = useState(false)
  const roleOptions = [
    {
      id: "employer" as const,
      title: t("onboarding.profileDetail.roleEmployer"),
      description: t("onboarding.profileDetail.roleEmployerDescription"),
      icon: Building2,
      features: [
        t("onboarding.profileDetail.roleEmployerFeature1"),
        t("onboarding.profileDetail.roleEmployerFeature2"),
        t("onboarding.profileDetail.roleEmployerFeature3"),
      ],
    },
    {
      id: "agent" as const,
      title: t("onboarding.profileDetail.roleAgent"),
      description: t("onboarding.profileDetail.roleAgentDescription"),
      icon: UserCheck,
      features: [
        t("onboarding.profileDetail.roleAgentFeature1"),
        t("onboarding.profileDetail.roleAgentFeature2"),
        t("onboarding.profileDetail.roleAgentFeature3"),
      ],
    },
  ]

  const title = isSignUp
    ? t("onboarding.profileDetail.title")
    : t("auth.emailAuth.welcomeBack")
  const subtitle = isSignUp
    ? t("onboarding.profileDetail.subtitle")
    : t("auth.emailAuth.signInToContinue")
  const buttonText = isSignUp
    ? t("auth.emailAuth.continueWithEmailSignUp")
    : t("auth.emailAuth.continueWithEmail")
  const sendingText = isSignUp
    ? t("auth.emailAuth.creatingAccount")
    : t("auth.emailAuth.sendingCode")

  useEffect(() => {
    if (!isSignUp || !captchaRef.current) return

    const captchaElement = captchaRef.current
    const updateCaptchaVisibility = () => {
      setHasCaptchaChallenge(captchaElement.childElementCount > 0)
    }

    updateCaptchaVisibility()

    const observer = new MutationObserver(updateCaptchaVisibility)
    observer.observe(captchaElement, {
      childList: true,
      subtree: true,
    })

    return () => observer.disconnect()
  }, [isSignUp])

  return (
    <div className="animate-in fade-in slide-in-from-left-4 duration-300">
      <Form {...loginForm}>
        <form className="space-y-6" onSubmit={loginForm.handleSubmit(onLogin)}>
          {/* Header */}
          <div className="space-y-2">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {title}
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              {subtitle}
            </p>
          </div>

          {!isSignUp && (
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-3 text-muted-foreground">
                  {t("auth.emailAuth.signInWithEmail")}
                </span>
              </div>
            </div>
          )}

          {isSignUp && (
            <>
              <FormField
                control={loginForm.control}
                name="user_type"
                render={({ field }) => (
                  <FormItem className="space-y-3">
                    <FormLabel className="text-sm font-medium text-gray-700">
                      {t("onboarding.profileDetail.iamAn")}
                    </FormLabel>
                    <FormControl>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {roleOptions.map((role) => {
                          const isSelected = field.value === role.id
                          const Icon = role.icon

                          return (
                            <button
                              key={role.id}
                              type="button"
                              onClick={() => field.onChange(role.id)}
                              className={cn(
                                "relative flex flex-col items-start p-4 rounded-xl border-2 text-left transition-all duration-200 group",
                                "hover:border-primary/50 hover:shadow-md",
                                isSelected
                                  ? "border-primary bg-primary/5 shadow-sm"
                                  : "border-gray-200 bg-white hover:bg-gray-50/50"
                              )}
                            >
                              {isSelected && (
                                <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                                  <Check className="w-3 h-3 text-white" />
                                </div>
                              )}

                              <div
                                className={cn(
                                  "w-10 h-10 rounded-lg flex items-center justify-center mb-3 transition-colors duration-200",
                                  isSelected
                                    ? "bg-primary text-white"
                                    : "bg-gray-100 text-gray-600 group-hover:bg-primary/10 group-hover:text-primary"
                                )}
                              >
                                <Icon className="w-5 h-5" />
                              </div>

                              <span
                                className={cn(
                                  "font-semibold text-base mb-1 transition-colors duration-200",
                                  isSelected ? "text-primary" : "text-gray-900"
                                )}
                              >
                                {role.title}
                              </span>

                              <span className="text-xs text-gray-500 leading-relaxed">
                                {role.description}
                              </span>

                              <ul className="mt-3 space-y-1 hidden sm:block">
                                {role.features.map((feature) => (
                                  <li
                                    key={feature}
                                    className="flex items-center gap-1.5 text-xs text-gray-400"
                                  >
                                    <div
                                      className={cn(
                                        "w-1 h-1 rounded-full",
                                        isSelected
                                          ? "bg-primary"
                                          : "bg-gray-300"
                                      )}
                                    />
                                    {feature}
                                  </li>
                                ))}
                              </ul>
                            </button>
                          )
                        })}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-3 text-muted-foreground">
                    {t("onboarding.profileDetail.yourDetails")}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={loginForm.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700">
                        {t("onboarding.profileDetail.firstName")}
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t(
                            "onboarding.profileDetail.firstNamePlaceholder"
                          )}
                          className="h-12 rounded-xl border-2 border-gray-200 px-4 focus-visible:ring-primary/20"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={loginForm.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700">
                        {t("onboarding.profileDetail.lastName")}
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t(
                            "onboarding.profileDetail.lastNamePlaceholder"
                          )}
                          className="h-12 rounded-xl border-2 border-gray-200 px-4 focus-visible:ring-primary/20"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </>
          )}

          {/* Email Input */}
          <FormField
            control={loginForm.control}
            name="email_address"
            render={({ field }) => (
              <FormItem className="space-y-2 w-full">
                <FormLabel className="text-sm font-medium text-gray-700">
                  {t("auth.emailAuth.emailAddress")}
                </FormLabel>
                <FormControl>
                  <Input
                    disabled={isLoading}
                    placeholder={t("auth.emailAuth.emailPlaceholder")}
                    className="h-12 rounded-xl border-2 border-gray-200 px-4 text-base focus-visible:ring-primary/20"
                    autoComplete="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {isSignUp && (
            <div
              className={[
                "overflow-hidden transition-all duration-300",
                hasCaptchaChallenge
                  ? "max-h-40 opacity-100"
                  : "max-h-0 opacity-0",
              ].join(" ")}
              aria-hidden={!hasCaptchaChallenge}
            >
              {/* Required by Clerk custom sign-up flows for Smart CAPTCHA rendering. */}
              <div
                id="clerk-captcha"
                ref={captchaRef}
                className="flex justify-center sm:justify-start"
              />
            </div>
          )}

          {/* Submit Button */}
          <Button
            disabled={isLoading}
            className="w-full h-12 text-base font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
            type="submit"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                {sendingText}
              </span>
            ) : (
              buttonText
            )}
          </Button>

          {/* Help text */}
          <p className="text-xs text-center text-muted-foreground">
            {isSignUp
              ? t("onboarding.profileDetail.helperText")
              : t("auth.emailAuth.verificationCodeSent")}
          </p>
        </form>
      </Form>
    </div>
  )
}

interface OtpVerificationScreenProps {
  isLoading: boolean
  onBack?: () => void
  sentEmailAddress: string
  otpForm: UseFormReturn<OtpFormData>
  onOtpSubmit: (data: OtpFormData) => void
  onResendOtp?: () => void
}

const OtpVerificationScreen = ({
  isLoading,
  onBack,
  sentEmailAddress,
  otpForm,
  onOtpSubmit,
  onResendOtp,
}: OtpVerificationScreenProps) => {
  const { t } = useTranslation()

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
      <Form {...otpForm}>
        <form
          className="space-y-6"
          onSubmit={otpForm.handleSubmit(onOtpSubmit)}
        >
          {/* Header with back button */}
          <div className="space-y-3">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors duration-200"
              >
                <ArrowLeft className="h-4 w-4" />
                {t("auth.emailAuth.back")}
              </button>
            )}

            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <ShieldCheck className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {t("auth.emailAuth.verifyYourEmail")}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {t("auth.emailAuth.enterSixDigitCode")}
                </p>
              </div>
            </div>
          </div>

          {/* Info text */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 space-y-1">
            <p className="text-sm font-medium text-gray-700">
              {t("auth.emailAuth.verificationCodeSentTo")}
            </p>
            <p className="text-sm text-primary font-semibold break-all">
              {sentEmailAddress}
            </p>
            <p className="text-xs text-muted-foreground pt-1">
              {t("auth.emailAuth.checkInboxAndSpam")}
            </p>
          </div>

          {/* OTP Input */}
          <FormField
            control={otpForm.control}
            name="otp"
            render={({ field }) => (
              <FormItem className="space-y-3 w-full">
                <FormLabel className="text-sm font-medium text-gray-700">
                  {t("auth.emailAuth.verificationCode")}
                </FormLabel>
                <FormControl>
                  <InputOTP
                    maxLength={6}
                    pattern={REGEXP_ONLY_DIGITS}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={isLoading}
                    autoFocus
                    containerClassName="w-full"
                  >
                    <InputOTPGroup className="w-full">
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </FormControl>
                <FormMessage className="text-center sm:text-left" />
              </FormItem>
            )}
          />

          {/* Submit Button */}
          <Button
            disabled={isLoading}
            className="w-full h-12 text-base font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
            type="submit"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("auth.emailAuth.verifying")}
              </span>
            ) : (
              t("auth.emailAuth.verifyAndContinue")
            )}
          </Button>

          {/* Resend OTP */}
          <div className="text-center pt-2">
            <span className="text-sm text-muted-foreground">
              {t("auth.emailAuth.didntReceiveCode")}{" "}
            </span>
            <button
              type="button"
              onClick={onResendOtp}
              disabled={isLoading}
              className="text-sm font-semibold text-primary hover:text-primary/80 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("auth.emailAuth.resend")}
            </button>
          </div>
        </form>
      </Form>
    </div>
  )
}

// Backwards compatibility export – can be cleaned up once all usages are updated.
export const UserWhatsAppAuthForm = UserEmailAuthForm
